import { z } from 'zod'
import { uuid } from './common.ts'

// Analysis is the proprietor's view of the business: what was sold, to
// whom, by which doctor, and what the statutory register says. It is
// read-only by construction -- there is no analytics:write -- so every
// endpoint here is a SELECT and nothing in this module can change a record.

// ---------------------------------------------------------------- filters

/**
 * The one filter set every chart is asked for. A chart ignores the keys it
 * has no column for (`CHARTS[id].filters` says which it honours), so the
 * filter bar can stay put while the person moves between charts instead of
 * resetting itself each time.
 */
export const analyticsFilters = z.object({
  // Inclusive dates in the clinic's own calendar, not UTC instants: a bill
  // paid at 11pm on the 31st belongs to the 31st in Jodhpur, and would slide
  // into the next month if these were compared as timestamps.
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  // How finely to cut the time axis. 'auto' picks from the span so a year
  // does not arrive as 365 unreadable columns.
  bucket: z.enum(['auto', 'day', 'week', 'month']).default('auto'),
  doctorId: uuid.optional(),
  productId: uuid.optional(),
  patientId: uuid.optional(),
  district: z.string().trim().max(80).optional(),
  substance: z.enum(['poppy', 'opium', 'tola']).optional(),
  paymentMethod: z.enum(['cash', 'upi', 'card', 'other']).optional(),
})
export type AnalyticsFilters = z.infer<typeof analyticsFilters>

export const FILTER_KEYS = [
  'doctorId', 'productId', 'patientId', 'district', 'substance', 'paymentMethod',
] as const
export type FilterKey = (typeof FILTER_KEYS)[number]

// ------------------------------------------------------------- the shapes

/**
 * The forms a chart may take. Deliberately few: every one of these is a
 * form somebody can read without being taught it.
 *
 * `line`    change over time, one or more series on ONE axis
 * `bar`     magnitude across a time axis (vertical columns)
 * `hbar`    magnitude across named things, ranked (horizontal, labels read)
 * `stacked` parts of a whole across a time axis
 * `histogram` a distribution over ordered numeric bins
 * `funnel`  an ordered pipeline where each stage is a subset of the last
 * `multiples` several small charts sharing an x axis but NOT a y scale
 * `tiles`   headline numbers, no plot
 * `table`   a list that is genuinely a list
 */
export const CHART_FORMS = [
  'line', 'bar', 'hbar', 'stacked', 'histogram', 'funnel', 'multiples', 'tiles', 'table',
] as const
export type ChartForm = (typeof CHART_FORMS)[number]

/** What the numbers are, so the axis and tooltip can label themselves. */
export const UNITS = ['money', 'count', 'qty', 'kg', 'years', 'percent', 'mixed'] as const
export type Unit = (typeof UNITS)[number]

export type Series = {
  key: string
  label: string
  /** Money is INTEGER PAISE; never rupees as a float. */
  data: (number | null)[]
  /** Set on a `multiples` panel whose unit differs from the chart's. */
  unit?: Unit
  /**
   * `multiples` only: which small chart this series is drawn in. Two
   * series share a panel only when they share a scale -- systolic and
   * diastolic pressure do, pulse and SpO2 do not. This is how the form
   * refuses a second y-axis: a measure on a different scale gets its own
   * panel instead of a right-hand axis nobody reads correctly.
   */
  panel?: string
}

export type Tile = { label: string; value: string; sub?: string }

export type ChartPayload = {
  id: ChartId
  form: ChartForm
  unit: Unit
  /** x-axis labels, one per index in every series' `data`. */
  categories: string[]
  series: Series[]
  tiles?: Tile[]
  table?: { columns: string[]; rows: (string | number | null)[][] }
  /** Said out loud under the chart when the data needed qualifying. */
  note?: string
}

// ----------------------------------------------------------- the catalogue

export const CHART_GROUPS = [
  { id: 'money', label: 'Money', blurb: 'What was billed, collected and owed.' },
  { id: 'register', label: 'NDPS register', blurb: 'The statutory register and what it records.' },
  { id: 'patients', label: 'Patients', blurb: 'Who is coming, and from where.' },
  { id: 'operations', label: 'Operations', blurb: 'How the counter and the staff are running.' },
  { id: 'inspection', label: 'Inspection', blurb: 'What an inspector asks for.' },
] as const
export type GroupId = (typeof CHART_GROUPS)[number]['id']

export type ChartDef = {
  id: string
  group: GroupId
  title: string
  /** One line under the title saying what the chart answers. */
  question: string
  form: ChartForm
  unit: Unit
  filters: readonly FilterKey[]
  /** Whether the chart has a time axis the bucket control applies to. */
  timeAxis?: boolean
  /** A chart that is meaningless until a single patient is chosen. */
  needs?: 'patientId'
  /**
   * Set where a category can be so small it identifies the individual.
   * The query folds anything under the threshold into "Other".
   */
  suppressSmallCells?: boolean
  /** Half-width on the grid. Dense charts take the full row. */
  span?: 1 | 2
}

export const CHARTS = {
  // --- A. Money -----------------------------------------------------------
  'gst-summary': {
    id: 'gst-summary', group: 'money', form: 'tiles', unit: 'money',
    title: 'GST summary',
    question: 'What is the taxable value, CGST and SGST for the period?',
    filters: ['doctorId', 'productId', 'patientId', 'district', 'paymentMethod'],
    span: 2,
  },
  'revenue-over-time': {
    id: 'revenue-over-time', group: 'money', form: 'line', unit: 'money',
    title: 'Revenue over time',
    question: 'How much was collected, and is it rising or falling?',
    filters: ['doctorId', 'productId', 'patientId', 'district', 'paymentMethod'],
    timeAxis: true, span: 2,
  },
  'gst-over-time': {
    id: 'gst-over-time', group: 'money', form: 'stacked', unit: 'money',
    title: 'Tax collected over time',
    question: 'How does the CGST and SGST liability fall across the period?',
    filters: ['doctorId', 'productId', 'patientId', 'district', 'paymentMethod'],
    timeAxis: true, span: 2,
  },
  'top-products-revenue': {
    id: 'top-products-revenue', group: 'money', form: 'hbar', unit: 'money',
    title: 'Top products by revenue',
    question: 'Which medicines bring in the money?',
    filters: ['doctorId', 'patientId', 'district', 'paymentMethod'],
  },
  'top-products-quantity': {
    id: 'top-products-quantity', group: 'money', form: 'hbar', unit: 'qty',
    title: 'Top products by quantity',
    question: 'Which medicines move the most units?',
    filters: ['doctorId', 'patientId', 'district', 'paymentMethod'],
  },
  'outstanding-aged': {
    id: 'outstanding-aged', group: 'money', form: 'hbar', unit: 'money',
    title: 'Outstanding, by age',
    question: 'What has been invoiced but not paid, and how long has it been waiting?',
    filters: ['doctorId', 'patientId', 'district'],
  },
  'payment-mix': {
    id: 'payment-mix', group: 'money', form: 'hbar', unit: 'money',
    title: 'How people pay',
    question: 'Cash, UPI, card -- what is the split?',
    filters: ['doctorId', 'patientId', 'district'],
  },
  'average-bill': {
    id: 'average-bill', group: 'money', form: 'line', unit: 'money',
    title: 'Average bill value',
    question: 'Is the typical bill getting larger or smaller?',
    filters: ['doctorId', 'productId', 'patientId', 'district', 'paymentMethod'],
    timeAxis: true, span: 2,
  },
  'drafts-in-progress': {
    id: 'drafts-in-progress', group: 'money', form: 'table', unit: 'money',
    title: 'Drafts in progress',
    question: 'Which bills are sitting un-issued at the counter?',
    filters: ['doctorId', 'patientId'],
    span: 2,
  },

  // --- B. NDPS register ---------------------------------------------------
  'registrations-over-time': {
    id: 'registrations-over-time', group: 'register', form: 'bar', unit: 'count',
    title: 'New registrations',
    question: 'How many people entered the register in each period?',
    filters: ['patientId', 'district', 'substance'],
    timeAxis: true, span: 2,
  },
  'vitals-trajectory': {
    id: 'vitals-trajectory', group: 'register', form: 'multiples', unit: 'mixed',
    title: 'Vitals across the 30 days',
    question: 'On average, how do blood pressure, pulse and SpO2 move during treatment?',
    filters: ['patientId', 'district', 'substance'],
    span: 2,
  },
  'followup-compliance': {
    id: 'followup-compliance', group: 'register', form: 'bar', unit: 'percent',
    title: 'Follow-up compliance',
    question: 'What share of registrations still had a reading taken on each day?',
    filters: ['patientId', 'district', 'substance'],
    span: 2,
  },
  'dose-trend': {
    id: 'dose-trend', group: 'register', form: 'line', unit: 'qty',
    title: 'Dose over the schedule',
    question: 'Is this patient being tapered down?',
    filters: ['patientId', 'doctorId', 'productId'],
    needs: 'patientId', span: 2,
  },
  'opiate-quantity': {
    id: 'opiate-quantity', group: 'register', form: 'histogram', unit: 'kg',
    title: 'Declared monthly quantity',
    question: 'How much do registrants say they consume in a month?',
    filters: ['patientId', 'district', 'substance'],
  },
  'substance-split': {
    id: 'substance-split', group: 'register', form: 'hbar', unit: 'count',
    title: 'Substance declared',
    question: 'Poppy husk, opium or tola -- what is the mix?',
    filters: ['patientId', 'district'],
  },
  'addiction-period': {
    id: 'addiction-period', group: 'register', form: 'histogram', unit: 'years',
    title: 'Years of dependence',
    question: 'How long have registrants been using before they come in?',
    filters: ['patientId', 'district', 'substance'],
  },

  // --- C. Patients --------------------------------------------------------
  'patients-over-time': {
    id: 'patients-over-time', group: 'patients', form: 'bar', unit: 'count',
    title: 'New patients',
    question: 'How many new records are opened in each period?',
    filters: ['patientId', 'district'],
    timeAxis: true, span: 2,
  },
  'age-distribution': {
    id: 'age-distribution', group: 'patients', form: 'histogram', unit: 'count',
    title: 'Age distribution',
    question: 'Which age bands are being treated?',
    filters: ['patientId', 'district', 'doctorId'],
  },
  'sex-split': {
    id: 'sex-split', group: 'patients', form: 'hbar', unit: 'count',
    title: 'Sex',
    question: 'What is the split on record?',
    filters: ['patientId', 'district', 'doctorId'],
  },
  geography: {
    id: 'geography', group: 'patients', form: 'hbar', unit: 'count',
    title: 'Where patients come from',
    question: 'Which districts and tehsils does the dispensary actually serve?',
    filters: ['patientId', 'district', 'doctorId'],
    span: 2,
  },
  'returning-patients': {
    id: 'returning-patients', group: 'patients', form: 'hbar', unit: 'count',
    title: 'Return visits',
    question: 'How many patients come back for a second prescription, and a third?',
    filters: ['district', 'doctorId'],
  },
  occupation: {
    id: 'occupation', group: 'patients', form: 'hbar', unit: 'count',
    title: 'Occupation',
    question: 'What do registrants do for a living?',
    filters: ['district', 'doctorId'],
    suppressSmallCells: true,
  },
  education: {
    id: 'education', group: 'patients', form: 'hbar', unit: 'count',
    title: 'Education',
    question: 'How far did registrants study?',
    filters: ['district', 'doctorId'],
    suppressSmallCells: true,
  },
  caste: {
    id: 'caste', group: 'patients', form: 'hbar', unit: 'count',
    title: 'Caste',
    question: 'Recorded because the statutory form asks for it.',
    filters: ['district', 'doctorId'],
    suppressSmallCells: true,
  },

  // --- D. Operations ------------------------------------------------------
  'prescriptions-per-doctor': {
    id: 'prescriptions-per-doctor', group: 'operations', form: 'hbar', unit: 'count',
    title: 'Prescriptions per doctor',
    question: 'Who is writing the prescriptions?',
    filters: ['doctorId', 'patientId', 'district'],
  },
  'revenue-per-doctor': {
    id: 'revenue-per-doctor', group: 'operations', form: 'hbar', unit: 'money',
    title: 'Revenue per doctor',
    question: 'Which doctor’s prescriptions turn into collected money?',
    filters: ['doctorId', 'patientId', 'district', 'paymentMethod'],
  },
  'counter-throughput': {
    id: 'counter-throughput', group: 'operations', form: 'funnel', unit: 'count',
    title: 'Counter throughput',
    question: 'Of the prescriptions written, how many were sent, invoiced and paid?',
    filters: ['doctorId', 'patientId', 'district'],
    span: 2,
  },
  'never-prescribed': {
    id: 'never-prescribed', group: 'operations', form: 'table', unit: 'count',
    title: 'Products nobody prescribes',
    question: 'What is on the price list but never leaves the shelf?',
    filters: ['doctorId'],
    span: 2,
  },
  'staff-activity': {
    id: 'staff-activity', group: 'operations', form: 'hbar', unit: 'count',
    title: 'Staff activity',
    question: 'Who did how much, from the audit trail?',
    filters: [],
  },
  'refused-signins': {
    id: 'refused-signins', group: 'operations', form: 'bar', unit: 'count',
    title: 'Refused sign-ins',
    question: 'Is anyone outside the allowlist trying to get in?',
    filters: [],
    timeAxis: true,
  },

  // --- E. Inspection ------------------------------------------------------
  'narcotic-dispensed': {
    id: 'narcotic-dispensed', group: 'inspection', form: 'stacked', unit: 'qty',
    title: 'Quantity dispensed',
    question: 'How much of each medicine was actually invoiced in the period?',
    filters: ['doctorId', 'productId', 'patientId', 'district'],
    timeAxis: true, span: 2,
  },
  'register-entries': {
    id: 'register-entries', group: 'inspection', form: 'table', unit: 'mixed',
    title: 'Register entries',
    question: 'The register, as a list, for the period an inspector names.',
    filters: ['patientId', 'district', 'substance'],
    span: 2,
  },
  'declaration-coverage': {
    id: 'declaration-coverage', group: 'inspection', form: 'hbar', unit: 'count',
    title: 'Declaration version signed',
    question: 'Which wording did each registrant actually agree to?',
    filters: ['patientId', 'district', 'substance'],
    span: 2,
  },
} as const satisfies Record<string, ChartDef>

export type ChartId = keyof typeof CHARTS
export const CHART_IDS = Object.keys(CHARTS) as ChartId[]
export const chartId = z.enum(CHART_IDS as [ChartId, ...ChartId[]])

// Typed as ChartDef rather than the literal union: `satisfies` keeps every
// literal, which means a chart that omitted `span` has no such property at
// all and reading it is an error. Widening here gives callers one shape.
export const chartsInGroup = (g: GroupId): ChartDef[] =>
  CHART_IDS.map(id => CHARTS[id] as ChartDef).filter(c => c.group === g)
